import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import React, { useState } from "react";
import Modal from "components/UI/DialogModal";
import Form from "components/UI/Form";
import { CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import MuiButton from "components/UI/MuiButton";
import { IEmployee } from "models/employee";
import { ICountry } from "models/country";
import { ICompanyJob } from "models/companyJob";
import { useTranslation } from "react-i18next";
import { ICompanyDepartments } from "models/companyDepartment";

interface Props {
  open: boolean;
  loading: boolean;
  Employee: IEmployee;
  Countries: ICountry[];
  CompanyJobs: ICompanyJob[];
  CompanyDepartments: ICompanyDepartments[];
  onClose: () => void;
  onSubmit: () => void;
  onChanges: (name: string, value: any) => void;
}

const EditEmployee: React.FC<Props> = ({
  open,
  loading,
  Countries,
  CompanyJobs,
  CompanyDepartments,
  Employee,
  onClose,
  onSubmit,
  onChanges,
}) => {


  const [selectedDepartment, setSelectedDepartment] = useState<string>(Employee.companyDepartmentId);
  const [selectedJob, setSelectedJob] = useState<string>(Employee.companyJobId);
  const [filteredJobs, setFilteredJobs] = useState<ICompanyJob[]>(CompanyJobs.filter((job) => job.companyDepartmentId === Employee.companyDepartmentId));

  const countriesOptions = Countries.map((country) => {
    return { value: country.id, label: country.name };
  });

  const companyDepartmentsOptions = CompanyDepartments.map((department) => {
    return { value: department.id, label: department.name };
  });

  const handleChangeDepartment = (event: any) => {
    setSelectedDepartment(event.target.value);
    onChanges('CompanyDepartmentId', event.target.value)
    const jobsForDepartment = CompanyJobs.filter((job) => job.companyDepartmentId === event.target.value);
    setFilteredJobs(jobsForDepartment);
    onChanges('CompanyJobId', '')
  }

  const handleChangeJob = (event: any) => {
    setSelectedJob(event.target.value);
    onChanges('CompanyJobId', event.target.value)
  }



  const { t } = useTranslation();
  let basicFields1: IFormField[] = [
    
     {
      name: "EmployeeId",
      label: t("T_employee_id"),
      type: InputTypes.TEXT,
      internalLabel: true,
      defaultValue: Employee.employeeId,
      required: true,
    },{
      name: "FirstName",
      label: t("T_first_name"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_first_name"),
      internalLabel: true,
      defaultValue: Employee.firstName,
      required: true,
    },
    {
      name: "MiddleName",
      label: t("T_middle_name"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_middle_name"),
      internalLabel: true,
      defaultValue: Employee.middleName
    },
    {
      name: "LastName",
      label: t("T_last_name"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_last_name"),
      internalLabel: true,
      defaultValue: Employee.lastName,

      required: true,
    },
    {
      name: "FirstNameAr",
      label: `${t("T_first_name")} ${t("T_in_arabic")}`,
      type: InputTypes.TEXT,
      placeholder: `${t("T_enter_first_name")} ${t("T_in_arabic")}`,
      internalLabel: true,
      defaultValue: Employee.firstNameAr
    },
    {
      name: "MiddleNameAr",
      label: `${t("T_middle_name")} ${t("T_in_arabic")}`,
      type: InputTypes.TEXT,
      placeholder: `${t("T_enter_middle_name")} ${t("T_in_arabic")}`,
      internalLabel: true,
      defaultValue: Employee.middleNameAr
    },
    {
      name: "LastNameAr",
      label: `${t("T_last_name")} ${t("T_in_arabic")}`,
      type: InputTypes.TEXT,
      placeholder: `${t("T_enter_last_name")} ${t("T_in_arabic")}`,
      internalLabel: true,
      defaultValue: Employee.lastNameAr
    },


  ];


  let basicFields2: IFormField[] = [{
    name: "CountryCode",
    label: t("T_country_code"),
    type: InputTypes.COUNTRY,
    internalLabel: true,
    rows: 4,
    required: true,
    defaultValue: Employee?.countryCode,
  },]

  let basicFields3: IFormField[] = [

    {
      name: "PhoneNumber",
      label: t("T_phone_number"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_phone_number"),
      internalLabel: true,
      defaultValue: Employee.phoneNumber,
      rows: 4,
      required: true,
    },
  ]
  let basicFields4: IFormField[] = [
    {
      name: "Email",
      label: t("T_email"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_email"),
      internalLabel: true,
      defaultValue: Employee.email,
      rows: 4,
      required: true,
    },
    {
      name: "ResidenceCountryId",
      label: t("T_residence_country"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_residence_country"),
      options: countriesOptions,
      internalLabel: true,
      defaultValue: countriesOptions.find(
        (country) => country.value === Employee.residenceCountryId
      ),
      required: true,
    }
  ]

  let basicFields5: IFormField[] = [{
    name: "ProfilePicture",
    label: t("T_profile_picture"),
    type: InputTypes.FILE,
    internalLabel: true,
    defaultImageUrl: Employee.profilePicture,
  },]
  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t("T_edit") + ' ' + t("T_employees")}
        maxWidth="lg"
        children={
          <Stack spacing={2} p={2}>
            <Form fields={basicFields1} onChange={onChanges} />
            <div>
              <div style={{ display: 'flex' }}>
                <div style={{ width: '25%' }}>
                  <Form
                    fields={basicFields2}
                    onChange={onChanges}
                  />
                </div>

                <div style={{ width: '75%' }}>
                  <Form
                    fields={basicFields3}
                    onChange={onChanges}
                  />
                </div>
              </div>
            </div>
            <Form fields={basicFields4} onChange={onChanges} />
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('T_department')}</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={handleChangeDepartment}
                label={t('T_department')}
              >
                {companyDepartmentsOptions.map((dep) => (
                  <MenuItem key={dep.value} value={dep.value}>
                    {dep.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={!selectedDepartment}>
              <InputLabel>{t('T_job')}</InputLabel>
              <Select
                value={selectedJob}
                onChange={handleChangeJob}
                label={t('T_job')}
              >
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job: ICompanyJob) => (
                    <MenuItem key={job.id} value={job.id}>
                      {job.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{'No jobs available'}</MenuItem>
                )}
              </Select>
            </FormControl>
            <Form fields={basicFields5} onChange={onChanges} />
          </Stack>
        }
        actions={
          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
            px={1}
          >
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "warning.main", color: "white" }}
              size="large"
              onClick={onClose}
            >
              {t("T_cancel")}
            </MuiButton>
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "primary.main", color: "white" }}
              disabled={loading}
              size="large"
              onClick={onSubmit}
            >
              {t("T_save")}
              <CircularProgress
                size={10}
                sx={{ mx: 2, display: loading ? "" : "none" }}
              />
            </MuiButton>
          </Grid>
        }
      />
    </>
  );
};

export default EditEmployee;
